import { useState, useMemo } from 'react'
import { Plus, Send } from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { AnnouncementDrawer } from './AnnouncementDrawer'
import { useQuery } from '@pronteer/convex/react'
import { api } from '@pronteer/convex/api'
import type { Id } from '@pronteer/convex/dataModel'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { toast } from 'sonner'
import { ListSkeleton } from './ui/skeleton-presets'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { useCrewGetByIds } from '@/lib/hooks/crew/crew.hooks.queries'
import {
  useAnnouncementPublish,
  useAnnouncementUnpublish,
} from '@/lib/hooks/announcement'
import { AdminAnnouncementCard } from './AdminAnnouncementCard'
import { EmptyState } from './EmptyState'
import { AppLayout } from './AppLayout'
import { ViewAnnouncementDrawer } from './ViewAnnouncementDrawer'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { Button } from './ui/button'
import { Link } from '@tanstack/react-router'

interface AdminAnnouncementsProps {
  showAddButton: boolean
  showBackButton: boolean
}

export function AdminAnnouncements({
  showAddButton,
  showBackButton,
}: AdminAnnouncementsProps) {
  const [selectedAnnouncementId, setSelectedAnnouncementId] =
    useState<Id<'announcements'> | null>(null)
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [viewAnnouncementId, setViewAnnouncementId] =
    useState<Id<'announcements'> | null>(null)
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const { data: currentCrew } = useCurrentCrewMember()

  const announcements = useQuery(
    api.announcements.list,
    currentCrew?.companyId && currentCrew?.id ? { companyId: currentCrew.companyId, crewId: currentCrew.id } : 'skip'
  )
  const loading = announcements === undefined

  const { mutateAsync: unpublishAnnouncementMutation } = useAnnouncementUnpublish()
  const { mutateAsync: publishAnnouncementMutation } = useAnnouncementPublish()

  // Collect all unique crewIds from readers across all announcements for batch crew fetch
  const allReaderCrewIds = useMemo(() => {
    if (!announcements) return []
    const ids = new Set<string>()
    for (const a of announcements) {
      for (const id of a.readByCrewIds ?? []) {
        ids.add(id)
      }
    }
    return Array.from(ids)
  }, [announcements])

  const { data: crewMembers } = useCrewGetByIds(allReaderCrewIds)

  // Map crewId -> crew member for quick lookup
  const crewByCrewId = useMemo(() => {
    const map = new Map<string, NonNullable<typeof crewMembers>[number]>()
    if (!crewMembers) return map
    for (const crew of crewMembers) {
      if (crew.id) map.set(crew.id, crew)
    }
    return map
  }, [crewMembers])

  const handleEdit = (id: Id<'announcements'>) => {
    setSelectedAnnouncementId(id)
    setIsEditDrawerOpen(true)
  }

  const handleUnpublishConfirm = async (id: Id<'announcements'>) => {
    try {
      await unpublishAnnouncementMutation({ id })
      toast.success('Announcement unpublished')
    } catch (error) {
      console.error('Error unpublishing announcement:', error)
      toast.error('Failed to unpublish announcement')
      throw error
    }
  }

  const handlePublish = async (id: Id<'announcements'>) => {
    // Recipients are derived server-side (all active company crew).
    try {
      await publishAnnouncementMutation({ id })
      toast.success('Announcement published')
    } catch (error) {
      console.error('Error publishing announcement:', error)
      toast.error('Failed to publish announcement')
      throw error
    }
  }

  const getViewedPercentage = (readCount: number, totalCount: number) => {
    if (totalCount === 0) return 0
    return Math.round((readCount / totalCount) * 100)
  }

  const isDesktop = useIsDesktop()

  const body = (
    <>
      {loading ? (
        <ListSkeleton />
      ) : (announcements ?? []).length === 0 ? (
        <EmptyState
          icon={Send}
          title="No Announcements Yet"
          description="Send announcements to your crew members. They'll receive notifications."
          addButtonText={isAdmin ? "New Announcement" : undefined}
          onAdd={isAdmin ? () => {
            hapticLight()
            setIsCreateDrawerOpen(true)
          } : undefined}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {(announcements ?? []).map((announcement) => {
            const readerCrew = (announcement.readByCrewIds ?? [])
              .map((id) => crewByCrewId.get(id))
              .filter(Boolean) as NonNullable<typeof crewMembers>[number][]

            return (
              <AdminAnnouncementCard
                key={announcement._id}
                title={announcement.title}
                message={announcement.message}
                createdAt={announcement.createdAt}
                viewedPercentage={getViewedPercentage(
                  announcement.readCount,
                  announcement.totalCount
                )}
                recipients={readerCrew}
                published={announcement.published !== false}
                onEdit={isAdmin ? () => handleEdit(announcement._id) : undefined}
                onUnpublishConfirm={() => handleUnpublishConfirm(announcement._id)}
                onPublish={isAdmin ? () => handlePublish(announcement._id) : undefined}
                onClick={() => {
                  hapticLight()
                  setViewAnnouncementId(announcement._id)
                  setIsViewDrawerOpen(true)
                }}
              />
            )
          })}
        </div>
      )}

      {/* Create Announcement Drawer */}
      <AnnouncementDrawer
        open={isCreateDrawerOpen}
        onOpenChange={() => setIsCreateDrawerOpen(false)}
        onSuccess={() => setIsCreateDrawerOpen(false)}
      />

      {/* Edit Announcement Drawer */}
      <AnnouncementDrawer
        open={isEditDrawerOpen}
        onOpenChange={() => {
          setIsEditDrawerOpen(false)
          setSelectedAnnouncementId(null)
        }}
        onSuccess={() => {
          setIsEditDrawerOpen(false)
          setSelectedAnnouncementId(null)
        }}
        announcementId={selectedAnnouncementId || undefined}
      />

      <ViewAnnouncementDrawer
        open={isViewDrawerOpen}
        onOpenChange={setIsViewDrawerOpen}
        announcement={
          (announcements ?? []).find((a) => a._id === viewAnnouncementId) ?? null
        }
      />
    </>
  )

  const breadcrumb = (
    <span>
      <Link to="/operations" className="hover:underline">
        Operations
      </Link>
      <span className="text-content-primary-subtle"> / Announcements</span>
    </span>
  )

  const addAction =
    showAddButton && isAdminLoaded && isAdmin ? (
      <Button
        roundness="round"
        variant="brand"
        size="sm"
        className="gap-2"
        onClick={() => {
          hapticLight()
          setIsCreateDrawerOpen(true)
        }}
      >
        New Announcement
        <Plus className="size-4 text-content-primary-white" />
      </Button>
    ) : undefined

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Announcements"
          breadcrumb={breadcrumb}
          action={addAction}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          showBackButton={showBackButton}
          title="Announcements"
          className="bg-surface-brand-L0"
          rightElement={
            showAddButton && isAdminLoaded && isAdmin ? (
              <button
                onClick={() => {
                  hapticLight()
                  setIsCreateDrawerOpen(true)
                }}
                className="bg-background-brand-bold flex items-center justify-center rounded-full size-8 hover:bg-background-brand-bold/90 transition-colors p-[7px]"
              >
                <Plus className="w-[14.75px] h-[14.75px] text-white" />
              </button>
            ) : undefined
          }
        />
      }
      className="pt-4 bg-surface-brand-L0"
    >
      {body}
    </AppLayout>
  )
}
